export interface AjaxError extends Error {
  code?: string
  status?: number
  payload?: any
}

export interface AjaxOptions {
  headers?: { [key: string]: string }
  payload?: any
  decorateError?: (err: AjaxError) => AjaxError
}

const ajax = (
  method: string,
  url: string,
  opts: AjaxOptions = {}
): Promise<any> => {
  const headers = opts.headers || {}

  let reqPayload: string | undefined
  if (opts.payload) {
    if (!headers['content-type']) {
      headers['content-type'] = 'application/json'
    }
    reqPayload = JSON.stringify(opts.payload)
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.addEventListener('load', () => {
      const { status, responseText } = xhr

      let resPayload: any = null
      try {
        resPayload = JSON.parse(responseText)
      } catch {
        // Ignore parse errors
      }

      if (status >= 200 && status <= 299) {
        resolve(resPayload)
      } else {
        let err: AjaxError = new Error(
          `API request failed. Method: ${method}. URL: ${url}. Status: ${status}. Response: ${responseText}`
        )
        err.code = 'AJAX_ERROR'
        err.status = status
        err.payload = resPayload
        if (opts.decorateError) {
          err = opts.decorateError(err)
        }
        reject(err)
      }
    })

    xhr.open(method, url)

    for (const name in headers) {
      if (Object.prototype.hasOwnProperty.call(headers, name)) {
        xhr.setRequestHeader(name, headers[name])
      }
    }

    if (reqPayload) {
      xhr.send(reqPayload)
    } else {
      xhr.send()
    }
  })
}

export function extractApiErrors(e: any): string[] {
  const defaultErrors = ['Could not connect to server. Please try again.']

  if (e.code !== 'AJAX_ERROR' || !e.payload || !e.payload.errors) {
    return defaultErrors
  }

  return e.payload.errors.map(
    ({ pointer, detail }: { pointer: string; detail: string }) => {
      const prefix = pointer ? humanPointer(pointer) + ': ' : ''
      return prefix + detail
    }
  )
}

export function humanPointer(pointer: string): string {
  pointer = pointer.replace(/^\/([^/]+)$/, '$1')
  pointer = pointer.substring(0, 1).toUpperCase() + pointer.substring(1)
  pointer = pointer.replace(/_/g, ' ')
  return pointer
}

export default ajax
